import { getBasePath, getPublicFrontendOrigin } from '@/lib/getPublicAppUrl'

// Import the headers module only when in a server context.  This util is
// intended to be used in both server and client contexts, so this conditional
// import is necessary to avoid a runtime error.
let headers: () => Headers
if (typeof window === 'undefined') {
  headers = require('next/headers').headers
}

/**
 * Origin of the current request, from its `origin` header or else its `host`
 * header. The scheme follows `x-forwarded-proto` so server-side requests to
 * ourselves reach an https tunnel or proxy instead of its http redirect, which
 * drops the session cookie.
 */
export function originFromRequestHeaders(headersList: Headers): string | null {
    const origin = headersList.get('origin')
    if (origin) {
        return new URL(origin).origin
    }

    const host = headersList.get('host')
    if (host) {
        const forwardedProtocol = headersList.get('x-forwarded-proto')?.split(',')[0]?.trim()
        const scheme = forwardedProtocol || (process.env.NEXT_PUBLIC_ENV === 'preview' ? 'https' : 'http')
        return `${scheme}://${host}`
    }

    return null
}

/**
 * Public app base URL for same-origin API and return-URL construction.
 * Includes optional `NEXT_PUBLIC_BASE_PATH` (e.g. `https://uat.mercy.net/carefront`).
 *
 * Prefer `${getRequestOrigin()}/visit/...` or `getPublicAppUrl`. Do not use
 * `new URL('/path', getRequestOrigin())` when a basePath is set — an absolute
 * path replaces the mount segment. For true origin-only checks, use
 * `new URL(getRequestOrigin()).origin`.
 */
export function getRequestOrigin(): string {
    // Client-side context
    if (typeof window !== 'undefined') {
        // eslint-disable-next-line nile/no-root-relative-app-url -- this helper appends the basePath
        return `${window.location.origin}${getBasePath()}`
    }

    // Server-side context
    try {
        if (
            process.env.NEXT_PUBLIC_ENV !== 'production' &&
            process.env.NEXT_PUBLIC_ENV !== 'staging' &&
            process.env.NEXT_PUBLIC_ENV !== 'pre-production'
        ) {
            const requestOrigin = originFromRequestHeaders(headers())
            if (requestOrigin) {
                return `${requestOrigin}${getBasePath()}`
            }
        }
    } catch (error) {
        // If headers() fails (e.g., not in a server context), continue to fallback
    }
    
    // Fallback to environment variable if no other options are available
    return `${getPublicFrontendOrigin()}${getBasePath()}`
}
